import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Copy, Check, ExternalLink, Trash2, Loader } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { generateShareUrl } from "@/integrations/sharing/sharingService";
import type { SharedLink } from "@/integrations/supabase/types";

interface ShareManagementDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  recordName: string;
  sharedLink: SharedLink;
  onDeactivate: (linkId: string) => Promise<boolean>;
  onCopyLink: (token: string) => Promise<void>;
  isLoading: boolean;
}

export const ShareManagementDialog = ({
  open,
  onOpenChange,
  recordName,
  sharedLink,
  onDeactivate,
  onCopyLink,
  isLoading,
}: ShareManagementDialogProps) => {
  const [copied, setCopied] = useState(false);
  const [showDeactivateConfirm, setShowDeactivateConfirm] = useState(false);
  const { toast } = useToast();

  const shareUrl = generateShareUrl(sharedLink.token);

  const handleCopyLink = async () => {
    try {
      await onCopyLink(sharedLink.token);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      toast({
        title: "Ошибка",
        description: "Не удалось скопировать ссылку",
        variant: "destructive",
      });
    }
  };

  const handleOpenLink = () => {
    if (shareUrl && shareUrl.trim()) {
      window.open(shareUrl, '_blank');
    }
  };

  const handleDeactivateClick = () => {
    setShowDeactivateConfirm(true);
  };

  const handleDeactivateConfirm = async () => {
    try {
      const success = await onDeactivate(sharedLink.id);
      if (success) {
        setShowDeactivateConfirm(false);
        onOpenChange(false);
        toast({
          title: "Ссылка деактивирована",
          description: "Доступ к записи по публичной ссылке заблокирован",
        });
      }
    } catch (error) {
      toast({
        title: "Ошибка",
        description: "Не удалось деактивировать ссылку",
        variant: "destructive",
      });
    }
  };

  const handleDialogOpenChange = (newOpen: boolean) => {
    onOpenChange(newOpen);
    if (!newOpen) {
      // Сброс состояния при закрытии
      setCopied(false);
      setShowDeactivateConfirm(false);
    }
  };

  const formatDate = (dateString: string) => {
    try {
      return new Date(dateString).toLocaleDateString('ru-RU', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return 'Неизвестно';
    }
  };

  const getExpirationText = () => {
    if (!sharedLink.expires_at) {
      return 'Бессрочная';
    }
    
    try {
      const expiryDate = new Date(sharedLink.expires_at);
      return expiryDate.toLocaleDateString('ru-RU', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      });
    } catch {
      return 'Неизвестно';
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={handleDialogOpenChange}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Управление публичной ссылкой</DialogTitle>
            <DialogDescription>
              Управление ссылкой на запись "{recordName}"
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {/* Поле с URL */}
            <div className="space-y-2">
              <Label htmlFor="share-link">Публичная ссылка</Label>
              <div className="flex space-x-2">
                <Input
                  id="share-link"
                  value={shareUrl}
                  readOnly
                  className="flex-1"
                />
                <Button
                  size="icon"
                  variant="outline"
                  onClick={handleCopyLink}
                  disabled={isLoading}
                  aria-label="Копировать ссылку"
                  className="shrink-0"
                >
                  {copied ? (
                    <Check className="h-4 w-4 text-green-500" />
                  ) : (
                    <Copy className="h-4 w-4" />
                  )}
                </Button>
                <Button
                  size="icon"
                  variant="outline"
                  onClick={handleOpenLink}
                  disabled={isLoading}
                  aria-label="Открыть ссылку"
                  className="shrink-0"
                >
                  <ExternalLink className="h-4 w-4" />
                </Button>
              </div>
            </div>

            {/* Статус ссылки */}
            <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
              <p className="text-sm text-green-800">
                ✅ Ссылка активна и доступна для просмотра
              </p>
            </div>

            {/* Информация о ссылке */}
            <div className="text-xs text-muted-foreground space-y-1">
              <p><strong>Создана:</strong> {formatDate(sharedLink.created_at)}</p>
              <p><strong>Срок действия:</strong> {getExpirationText()}</p>
              <p>• Доступ без авторизации</p>

            </div>

            {/* Действия */}
            <div className="flex justify-between pt-4 border-t">
              <Button
                variant="destructive"
                onClick={handleDeactivateClick}
                disabled={isLoading}
              >
                {isLoading ? (
                  <>
                    <Loader className="h-4 w-4 mr-2 animate-spin" />
                    Деактивация...
                  </>
                ) : (
                  <>
                    <Trash2 className="h-4 w-4 mr-2" />
                    Деактивировать ссылку
                  </>
                )}
              </Button>
              <Button onClick={() => onOpenChange(false)} variant="outline">
                Закрыть
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Диалог подтверждения деактивации */}
      <AlertDialog open={showDeactivateConfirm} onOpenChange={setShowDeactivateConfirm}>
        <AlertDialogContent className="z-[85]">
          <AlertDialogHeader>
            <AlertDialogTitle>Деактивировать публичную ссылку?</AlertDialogTitle>
            <AlertDialogDescription>
              Ссылка станет недоступной. Все, кто попытается по ней перейти, увидят ошибку.
              Вы сможете создать новую ссылку в любое время.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Отменить</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDeactivateConfirm}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Деактивировать
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};